import * as angular from "angular";
import {
	decodeFileValue,
	encodeFileValue,
	errorsAtPath,
	FormRuntime,
	ValidationError
} from "../runtime";
import type { FileInputState } from "../FileInputValidation";
import confirmClear from "../../../behaviors/confirm-clear";
import type { ControlState } from "./control-state";
import fileInput from "./file-input";
import template from "./file-control.html";

export interface FileControlController {
	control: ControlState;
}

// AngularJS assigns bindings before constructing the controller.
// eslint-disable-next-line no-redeclare
export class FileControlController implements angular.IController {
	public static $inject = ["$scope"];

	public fileName = "No file selected";

	private generation = 0;

	private reader?: FileReader;

	private pendingRuntime?: FormRuntime;

	private pendingValue: unknown;

	private pendingPath?: string;

	private pendingError?: ValidationError;

	public constructor(private $scope: angular.IScope) {}

	public get fileError(): string | undefined {
		return errorsAtPath(this.control.session.runtime.inputErrors, this.control.path)[0];
	}

	private setFileInput(input?: FileInputState): void {
		this.control.session.runtime.setFileInput(this.control.path, input, this.control.schema);

		this.control.session.revision++;
	}

	public $onChanges(): void {
		if(this.reader && (this.pendingRuntime !== this.control.session.runtime
			|| this.pendingValue !== this.control.value || this.pendingPath !== this.control.path)) {
			this.cancelRead(true);
		}

		this.fileName = decodeFileValue(this.control.value)?.name
			?? (this.control.value ? "File selected" : "No file selected");
	}

	public $onDestroy(): void {
		this.cancelRead(true);
	}

	public fileSelected(file?: File): void {
		if(!file || !this.control.canEdit()) {
			return;
		}

		this.cancelRead();

		this.setFileInput({
			status: "selected",
			size: file.size
		});

		if(this.fileError) {
			return;
		}

		this.setFileInput({
			status: "reading",
			size: file.size
		});

		const reader = new FileReader();
		const generation = this.generation;
		this.reader = reader;
		this.pendingRuntime = this.control.session.runtime;
		this.pendingValue = this.control.value;
		this.pendingPath = this.control.path;
		this.pendingError = this.pendingRuntime.inputErrors.find(error => errorsAtPath([error], this.control.path).length > 0);

		const isCurrent = () => generation === this.generation
			&& this.pendingRuntime === this.control.session.runtime
			&& this.pendingPath === this.control.path
			&& this.pendingValue === this.control.value;

		reader.onload = () => {
			this.$scope.$evalAsync(() => {
				if(!isCurrent()) {
					return;
				}

				this.reader = undefined;

				if(!this.control.canEdit() || typeof reader.result !== "string") {
					this.setFileInput({ status: "failed" });
					return;
				}

				const base64 = reader.result.split(",")[1] ?? "";
				const value = this.control.schema.format === "file"
					? encodeFileValue(file.name, file.type, base64)
					: base64;

				this.setFileInput();
				this.control.change(value);
			});
		};
		reader.onerror = () => {
			this.$scope.$evalAsync(() => {
				if(isCurrent()) {
					this.reader = undefined;
					this.setFileInput({ status: "failed" });
				}
			});
		};

		try {
			reader.readAsDataURL(file);
		}catch(error) {
			this.reader = undefined;
			this.setFileInput({ status: "failed" });
		}
	}

	public clear(): void {
		if(!this.control.canEdit()) {
			return;
		}

		this.cancelRead();
		this.setFileInput();
		this.control.change(undefined);
	}

	private cancelRead(reportFailure = false): void {
		this.generation++;

		if(this.reader?.readyState === FileReader.LOADING) {
			this.reader.abort();
		}

		const pendingPath = this.pendingError && this.pendingRuntime?.getInputErrorPath(this.pendingError);

		if(this.reader && reportFailure && this.pendingRuntime && pendingPath !== undefined) {
			this.pendingRuntime.setFileInput(pendingPath, { status: "failed" }, this.control.schema);
			this.control.session.revision++;
		}

		this.reader = undefined;
		this.pendingError = undefined;
	}
}

export default angular.module("vesl.extensions.json-schema.file-control", [fileInput, confirmClear])
	.component("jsonSchemaFileControl", {
		template,
		controller: FileControlController,
		bindings: { control: "<" }
	})
	.name;
